import React from 'react';
import { useHtmlViewer } from './HtmlViewerContext';
import { useHighlightRects } from './hooks/useHighlightRects';
import { useIntersectionVisibility } from './hooks/useIntersectionVisibility';
import type { HighlightRect } from './HtmlViewerTypes';

interface HtmlViewerHighlightLayerProps {
  contentRef: React.RefObject<HTMLDivElement>;
  geometryRootRef: React.RefObject<HTMLDivElement>;
  scrollHeight: number;
}

export const HtmlViewerHighlightLayer = ({
  contentRef,
  geometryRootRef,
  scrollHeight,
}: HtmlViewerHighlightLayerProps) => {
  const { state, dispatch } = useHtmlViewer();
  const { annotations, activeAnnotationId } = state;

  const allRects = useHighlightRects(annotations, contentRef, geometryRootRef);
  const visibleRects = useIntersectionVisibility(allRects, geometryRootRef);

  const handleRectClick = (
    rect: HighlightRect,
    event: React.MouseEvent<SVGRectElement>
  ) => {
    event.stopPropagation();
    dispatch({ type: 'SET_ACTIVE_ANNOTATION', payload: rect.annotationId });
    dispatch({
      type: 'SET_MENU_POSITION',
      payload: { x: event.clientX, y: event.clientY },
    });
    dispatch({ type: 'SET_PENDING_RANGE', payload: null });
  };

  if (visibleRects.length === 0) return null;

  return (
    <svg
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: scrollHeight,
        pointerEvents: 'none',
        overflow: 'visible',
        zIndex: 1,
      }}
    >
      {visibleRects.map((rect, idx) => {
        const isActive = rect.annotationId === activeAnnotationId;
        const key = `${rect.annotationId}-${idx}`;

        return (
          <rect
            key={key}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            fill={rect.color}
            fillOpacity={isActive ? 0.55 : 0.3}
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            onClick={e => handleRectClick(rect, e)}
          />
        );
      })}
    </svg>
  );
};
